/**
 * ConditionSelectionPanelTOC.js
 *
 * A TOC-specific variant of ConditionSelectionPanel with:
 *
 *   - Optional amber outer panel + "Not included in final report" footer
 *     (SSI Review, and TOC-style DD pages via variant="non-report").
 *     Orgs that still make differential decisions keep variant="default".
 *   - Subtitle always visible regardless of confirmed/expanded state
 *   - Smooth animated transition between collapsed and expanded states
 *   - Brand-primary colour (not violet) for selected conditions and the
 *     Confirm button in the expanded picker
 *   - Collapsed state styled to match the "Condition(s) Referred For" panel
 *
 * The original ConditionSelectionPanel is left untouched so that all
 * non-TOC workflows continue to behave exactly as before.
 *
 * AUTISM-SPECIFIC OPT-IN PROPS
 * The Adult Autism review pages need a slightly different treatment from the
 * ADHD pages that share this component, so three additive, default-off props
 * exist. ADHD usages pass none of them and therefore look/behave EXACTLY as
 * before:
 *   - density="compact"          → shrinks the header/instruction/content fonts
 *                                  so the panel matches the smaller Autism review
 *                                  panels (e.g. NotInReportNotesPanel).
 *   - searchable                 → renders a substring search box above the
 *                                  categorised list that filters options live
 *                                  (mirrors the "move note" dropdown search).
 *   - showSelectedBullets        → renders a running bullet list of the currently
 *                                  selected conditions between the instruction and
 *                                  the picker, so long lists stay easy to track.
 */
import React from 'react';
import { XIcon, SearchIcon } from '@heroicons/react/solid';

function ConditionSelectionPanelTOC({
  title,
  subtitle,
  /** Optional node beside the title (e.g. DD review "Last updated …" in gray italic). */
  titleMeta = null,
  selectedOptions,
  isMultiSelect,
  isConfirmed,
  isDisabled,
  onSelect,
  onConfirm,
  onChangeChoice,
  conditions = {},
  allowEmptyConfirm = false,
  canEdit = true,
  /**
   * Panel chrome: "non-report" = amber + exclusion footer (SSI Review, and
   * DD pages for orgs that skip differential decisions). "default" = gray
   * panel with normal padding (decision-making orgs).
   */
  variant = 'default',
  /** 'comfortable' (ADHD default) | 'compact' (Adult Autism review parity). */
  density = 'comfortable',
  /** When true, show a substring search box above the categorised list. */
  searchable = false,
  /** When true, show a running bullet list of the selected conditions. */
  showSelectedBullets = false,
  /** Placeholder for the search box (only used when searchable). */
  searchPlaceholder = 'Search conditions...',
}) {
  const isNonReportPanel = variant === 'non-report';
  const isCompact = density === 'compact';
  const [showScrollHint, setShowScrollHint] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const scrollContainerRef = React.useRef(null);

  // Hide scroll hint when the user reaches the bottom of the conditions list
  const handleScroll = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const isAtBottom =
      Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) < 10;
    setShowScrollHint(!isAtBottom);
  }, []);

  // Re-measure when expanding or when the conditions list changes so the hint
  // shows whenever the picker scrolls (same behaviour as ConditionSelectionPanel).
  React.useEffect(() => {
    if (isConfirmed || !canEdit) return;

    const el = scrollContainerRef.current;
    if (!el) return;

    const updateScrollHint = () => {
      setShowScrollHint(el.scrollHeight > el.clientHeight);
    };

    el.addEventListener('scroll', handleScroll);
    updateScrollHint();

    // Defer once after expand animation so layout height is stable
    const raf = requestAnimationFrame(updateScrollHint);

    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', handleScroll);
    };
    // `search` is included so the hint re-measures after the list is filtered.
  }, [handleScroll, isConfirmed, canEdit, conditions, search]);

  const hasSelections = Array.isArray(selectedOptions) && selectedOptions.length > 0;
  const confirmEnabled = hasSelections || (allowEmptyConfirm && isConfirmed !== null);

  // ── Compact-density font/spacing classes ─────────────────────────────────
  // In compact mode every text size steps down one notch so the panel matches
  // the other (smaller) Adult Autism review panels. Comfortable mode keeps the
  // original ADHD sizing untouched.
  const titleClass = isCompact ? 'font-bold text-sm text-gray-800' : 'font-bold text-lg';
  const subtitleClass = isCompact ? 'text-xs text-gray-500 mt-0.5' : 'text-sm text-gray-600 mt-1';
  const summaryTextClass = isCompact ? 'text-sm' : 'text-base';
  const categoryHeaderClass = isCompact ? 'text-xs font-semibold' : 'text-sm font-semibold';
  const optionTextClass = isCompact ? 'text-xs' : 'text-sm';
  const confirmBtnSizeClass = isCompact ? 'py-1.5 px-3 text-sm' : 'py-2 px-4';

  // ── Substring search over the categorised conditions ─────────────────────
  // Only filters when searchable AND the clinician has typed something. Matching
  // is case-insensitive on the condition label; categories that end up with no
  // matches are dropped so the picker never shows an empty section header.
  const trimmedSearch = search.trim().toLowerCase();
  const filteredConditions = React.useMemo(() => {
    if (!searchable || !trimmedSearch) return conditions;
    const out = {};
    for (const [category, options] of Object.entries(conditions)) {
      const matched = options.filter((o) =>
        (o.icd_11_label || '').toLowerCase().includes(trimmedSearch),
      );
      if (matched.length > 0) out[category] = matched;
    }
    return out;
  }, [searchable, trimmedSearch, conditions]);

  const hasFilterResults = Object.keys(filteredConditions).length > 0;

  // Shared transition style for the two content sections that slide in/out.
  // We animate max-height (for layout flow) and opacity (for fade) together.
  // The collapsed summary uses a modest max-height cap; the expanded picker
  // uses a large cap to accommodate the full conditions list.
  const summaryTransition = {
    overflow: 'hidden',
    transition: 'max-height 0.35s ease, opacity 0.25s ease',
    maxHeight: isConfirmed ? '600px' : '0px',
    opacity: isConfirmed ? 1 : 0,
  };

  const pickerTransition = {
    overflow: 'hidden',
    transition: 'max-height 0.35s ease, opacity 0.25s ease',
    maxHeight: !isConfirmed ? '1400px' : '0px',
    opacity: !isConfirmed ? 1 : 0,
  };

  const panelClassName = isNonReportPanel
    ? 'bg-amber-50 text-left rounded-2xl border border-amber-200 px-6 pt-6 pb-2 shadow-md flex flex-col'
    : 'bg-gray-50 text-left rounded-2xl border border-gray-200 p-6 shadow-md flex flex-col';

  return (
    <div className={panelClassName}>

      {/* ── Header — always visible ───────────────────────────────────────── */}
      <div className="flex justify-between items-start">
        <div className="flex-1 mr-4">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h4 className={titleClass}>{title}</h4>
            {titleMeta}
          </div>
          {subtitle && (
            <p className={subtitleClass}>{subtitle}</p>
          )}
        </div>

        {canEdit && (
          <button
            onClick={isConfirmed ? onChangeChoice : onConfirm}
            disabled={!isConfirmed && !confirmEnabled}
            className={`${confirmBtnSizeClass} rounded-md shadow-md transition-all duration-300 font-semibold whitespace-nowrap self-start mt-0.5
              ${isConfirmed
                ? 'bg-primary text-white hover:opacity-90'
                : confirmEnabled
                  ? 'bg-primary text-white hover:opacity-90'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
          >
            {isConfirmed ? 'Change Choice' : 'Confirm Choice'}
          </button>
        )}
      </div>

      {/* ── Collapsed summary (slides in when confirmed) ──────────────────── */}
      <div style={summaryTransition}>
        <div className="mt-3">
          {hasSelections ? (
            <div className="rounded-lg border border-gray-200 bg-gray-100 px-4 py-3 space-y-2">
              {selectedOptions.map((option, i) => (
                <p key={i} className={`${summaryTextClass} text-gray-700 flex items-start gap-2`}>
                  <span className="text-gray-400 mt-0.5 select-none">•</span>
                  <span className="font-semibold">{option}</span>
                </p>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-100 px-4 py-3">
              <p className={`${summaryTextClass} text-gray-500 italic`}>
                No additional conditions identified.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Expanded picker (slides in when not confirmed) ────────────────── */}
      <div style={pickerTransition}>
        {canEdit ? (
          <div className="relative mt-2">
            {/* Running list of what's already selected — sits between the
                instruction and the picker so a long list stays easy to track. */}
            {showSelectedBullets && hasSelections && (
              /* Styled to match the "Condition(s) Referred For" bullet
                 (AutismReferredForPanel): same gray-100 box, text-sm semibold
                 bullets — for visual consistency between the two panels. No
                 "Selected (N)" preface. */
              <div className="rounded-lg border border-gray-200 bg-gray-100 p-3 mb-2">
                <ul className="space-y-1">
                  {selectedOptions.map((option) => (
                    <li key={option} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-gray-400 mt-0.5 select-none">•</span>
                      <span className="font-semibold flex-1">{option}</span>
                      <button
                        type="button"
                        onClick={() => onSelect(option)}
                        className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5"
                        title={`Remove ${option}`}
                        aria-label={`Remove ${option}`}
                      >
                        <XIcon className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Substring search over the categorised list. */}
            {searchable && (
              <div className="relative mb-1">
                <SearchIcon className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-8 pr-8 py-1.5 text-xs border border-gray-200 rounded-lg
                    focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}

            <div
              ref={scrollContainerRef}
              className="overflow-y-auto max-h-[400px] scroll-smooth"
            >
              {searchable && !hasFilterResults && (
                <p className="mt-4 px-1 text-xs text-gray-400 italic">
                  No conditions match &ldquo;{search.trim()}&rdquo;.
                </p>
              )}
              {Object.entries(filteredConditions).map(([category, options]) => (
                <div key={category} className="mt-4 border border-gray-300 rounded-lg">
                  <div className="rounded-lg p-4 bg-gray-100">
                    <h3 className={categoryHeaderClass}>{category}</h3>
                  </div>
                  <div className="rounded-lg p-4 bg-white">
                    <table className="w-full">
                      <tbody>
                        {options.map((option) => {
                          const isSelected = isMultiSelect
                            ? selectedOptions?.includes(option.icd_11_label)
                            : selectedOptions === option.icd_11_label;
                          const isOptionDisabled = isDisabled?.(option.icd_11_label);

                          return (
                            <tr
                              key={option.icd_11_label}
                              onClick={() => !isOptionDisabled && onSelect(option.icd_11_label)}
                              className={`
                                ${optionTextClass} rounded-lg transition-all duration-200
                                ${isSelected
                                  ? `bg-primary/15 outline outline-4 outline-primary font-semibold ${isOptionDisabled ? 'cursor-not-allowed' : ''}`
                                  : isOptionDisabled
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : 'hover:bg-primary/10 cursor-pointer'}
                              `}
                            >
                              <td className="py-2 pl-2">
                                {option.icd_11_label}
                                {option.symptomOverlapWithADHD && (
                                  <span className="italic text-gray-600 text-xs ml-2">
                                    (Symptom Overlap: {option.symptomOverlapWithADHD})
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-4 p-3 bg-gray-100 rounded-md">
            <p className="text-sm text-gray-600 italic">No selections have been made.</p>
          </div>
        )}
      </div>

      {/* Bottom-centre scroll hint — outside overflow:hidden picker so it stays visible */}
      {!isConfirmed && canEdit && (
        showScrollHint ? (
          <div className="mt-1 py-1 text-center pointer-events-none w-full">
            <span className="inline-flex items-center text-xs text-gray-600">
              Scroll to see more conditions
            </span>
          </div>
        ) : (
          <div className="mt-1 h-1" aria-hidden="true" />
        )
      )}

      {isNonReportPanel && (
        <p className="text-xs italic text-amber-900/80 text-right mt-1 mb-0">
          Not included in final report
        </p>
      )}
    </div>
  );
}

export default ConditionSelectionPanelTOC;
